<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

defineProps({
  zoom: { type: Number, default: 1 },
  wireStyle: { type: String, default: 'bezier' },
});

const emit = defineEmits([
  'zoomIn',
  'zoomOut',
  'zoomTo',
  'fit',
  'tidy',
  'update:wireStyle',
]);

const { t } = useI18n();
const [showZoomMenu, toggleZoomMenu] = useToggle();

const ZOOM_PRESETS = [0.5, 1, 1.5];

const zoomItems = computed(() => [
  ...ZOOM_PRESETS.map(value => ({
    action: 'zoom',
    value: String(value),
    label: `${value * 100}%`,
  })),
  {
    action: 'fit',
    value: 'fit',
    label: t('AGENT_BOTS.VISUAL_BUILDER.ZOOM_FIT'),
    icon: 'i-lucide-scan',
  },
]);

const onZoomAction = ({ action, value }) => {
  toggleZoomMenu(false);
  if (action === 'fit') emit('fit');
  else emit('zoomTo', Number(value));
};
</script>

<template>
  <div
    class="absolute z-20 flex items-center gap-1 p-1 -translate-x-1/2 border shadow-sm rounded-xl bottom-3 start-1/2 border-n-weak bg-n-solid-1"
  >
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-zoom-out"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.ZOOM_OUT')"
      @click="emit('zoomOut')"
    />
    <div v-on-clickaway="() => toggleZoomMenu(false)" class="relative">
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        class="w-14"
        :label="`${Math.round(zoom * 100)}%`"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.ZOOM_HINT')"
        @click="toggleZoomMenu()"
      />
      <DropdownMenu
        v-if="showZoomMenu"
        :menu-items="zoomItems"
        class="mb-2 -translate-x-1/2 bottom-full start-1/2 w-32"
        @action="onZoomAction"
      />
    </div>
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-zoom-in"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.ZOOM_IN')"
      @click="emit('zoomIn')"
    />
    <span class="w-px h-5 mx-0.5 bg-n-weak" />
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-scan"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.ZOOM_FIT')"
      @click="emit('fit')"
    />
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-layout-dashboard"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.TIDY_UP')"
      @click="emit('tidy')"
    />
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      :icon="wireStyle === 'step' ? 'i-lucide-route' : 'i-lucide-spline'"
      is-icon-only
      :title="t('AGENT_BOTS.VISUAL_BUILDER.WIRE_STYLE')"
      @click="
        emit('update:wireStyle', wireStyle === 'step' ? 'bezier' : 'step')
      "
    />
  </div>
</template>
